/**
 * NutSheetExportModal — date-range picker for the Nut Sheet Export to Excel.
 *
 * Defaults to the 1st of the currently-viewed month → the currently-viewed
 * date. Fetches the range via the range mode of GET /nut-sheet/entries, then
 * builds the workbook client-side (utils/nutSheetExport.ts). The Entries sheet
 * respects the page's row-visibility toggles; the Summary sheet always covers
 * the full range — both states are surfaced here so the operator knows what
 * they're getting before clicking Export.
 */

import React, { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { ReportDateRangeSelector } from '../reports/ReportDateRangeSelector';
import { INDIVIDUAL_LOCATIONS } from '../LocationDropdown';
import { listNutSheetEntriesRange } from '../../services/nutSheetApi';
import {
  NutSheetExportFilters,
  describeFilters,
  exportNutSheetToExcel,
} from '../../utils/nutSheetExport';

// Mirror of MAX_RANGE_DAYS in lambda/nut-sheet/shared/range.ts so users almost
// never see the server-side 400. Shared with NutSheetPrintModal.
export const MAX_RANGE_DAYS = 366;

interface NutSheetExportModalProps {
  locationId: number;
  /** The date currently viewed on the page (YYYY-MM-DD) — seeds the default range. */
  viewedDate: string;
  excludeFiReserve: boolean;
  filters: NutSheetExportFilters;
  onClose: () => void;
}

export function rangeSpanDays(startDate: string, endDate: string): number {
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const ms = Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd);
  return Math.floor(ms / 86_400_000) + 1;
}

export function validateRange(startDate: string, endDate: string): string | null {
  if (!startDate || !endDate) return 'Select both a start and end date.';
  if (startDate > endDate) return 'End date must be on or after start date.';
  if (rangeSpanDays(startDate, endDate) > MAX_RANGE_DAYS) {
    return `Date range cannot exceed ${MAX_RANGE_DAYS} days.`;
  }
  return null;
}

export const NutSheetExportModal: React.FC<NutSheetExportModalProps> = ({
  locationId,
  viewedDate,
  excludeFiReserve,
  filters,
  onClose,
}) => {
  const [startDate, setStartDate] = useState(`${viewedDate.slice(0, 8)}01`);
  const [endDate, setEndDate] = useState(viewedDate);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locationName =
    INDIVIDUAL_LOCATIONS.find((l) => l.id === locationId)?.name ?? `Location ${locationId}`;
  const validationError = validateRange(startDate, endDate);

  const handleExport = async () => {
    if (validationError) return;
    setExporting(true);
    setError(null);
    try {
      const response = await listNutSheetEntriesRange(
        locationId,
        startDate,
        endDate,
        excludeFiReserve,
      );
      if (!response.success || !response.data) {
        setError(response.error || 'Failed to load entries for the selected range.');
        return;
      }
      await exportNutSheetToExcel({
        locationName,
        startDate,
        endDate,
        entries: response.data.entries,
        summary: response.data.rangeSummary,
        excludeFiReserve,
        filters,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900">Export to Excel</h3>
        <p className="mt-1 text-sm text-gray-600">
          {locationName} — choose the date range to export.
        </p>

        <div className="mt-4">
          <ReportDateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            disabled={exporting}
          />
        </div>

        <div className="mt-4 rounded-md bg-gray-50 border border-gray-200 p-3 text-xs text-gray-700 space-y-1">
          <p>
            <span className="font-semibold">F&amp;I Reserve:</span>{' '}
            {excludeFiReserve
              ? 'Excluded — F&I figures net of reserve; Entries sheet adds Reserve + Net F&I columns'
              : 'Included — full F&I figures'}
          </p>
          <p>
            <span className="font-semibold">Entries sheet row filters:</span>{' '}
            {describeFilters(filters)}
          </p>
          <p className="text-gray-500 italic">
            The Summary sheet always covers all entries in the range, regardless of row filters
            (same as the on-page summary cards). Change the toggles on the page before exporting
            to adjust either setting.
          </p>
        </div>

        {validationError && startDate && endDate && (
          <p className="mt-3 text-sm text-red-600">{validationError}</p>
        )}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={exporting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !!validationError}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md ${
              exporting || validationError
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {exporting ? 'Exporting…' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );
};
